import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Send, Mail, PhoneCall, Target, Clock, AlertTriangle, CalendarClock, Loader2,
  Plus, TrendingUp, CheckCircle2, ChevronRight, Kanban as KanbanIcon, ListChecks,
  GripVertical, MessageSquare, CalendarPlus, FileText, MapPin, ExternalLink, User, ChevronDown, ChevronsUpDown, Check,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { formatCurrency } from "../lib/utils-format";
import KanbanBoard from "../components/KanbanBoard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { DuplicateApnDialog, useDuplicateGuard } from "../components/DuplicateApnDialog";
import { toast } from "sonner";

const METRICS = [
  { key: "offers", label: "Offers", unit: ["offer", "offers"], icon: Send, blurb: "Qualified offers submitted weekly", accent: "text-sky-400", ring: "border-sky-500/40 bg-sky-500/5", bar: "bg-sky-400" },
  { key: "outreach", label: "On-Market Outreach", unit: ["outreach attempt", "outreach attempts"], icon: PhoneCall, blurb: "New listing-agent outreach per business day", accent: "text-emerald-400", ring: "border-emerald-500/40 bg-emerald-500/5", bar: "bg-emerald-400" },
  { key: "mailers", label: "Off-Market Mailers", unit: ["mailer", "mailers"], icon: Mail, blurb: "Owner mailers sent per business day", accent: "text-amber-400", ring: "border-amber-500/40 bg-amber-500/5", bar: "bg-amber-400" },
];

const STATUS_COLOR = {
  "New": "#2dd4bf", "Mailer Sent": "#f59e0b", "Mailer Received": "#fbbf24", "Contacted": "#38bdf8",
  "Qualified": "#34d399", "Unqualified": "#f87171", "Converted": "#a78bfa",
};

const money = (v) => (v == null || isNaN(v) ? "—" : formatCurrency(Math.round(v)));

const statusTone = (s) => {
  const t = String(s || "").toLowerCase();
  if (/(closed|won|acquired|under contract)/.test(t)) return "bg-emerald-500/15 text-emerald-400";
  if (/(negotiat|counter)/.test(t)) return "bg-amber-500/15 text-amber-400";
  if (/offer/.test(t)) return "bg-teal-500/15 text-teal-300";
  if (/(seller|respond|attention|hot)/.test(t)) return "bg-orange-500/15 text-orange-400";
  if (/converted/.test(t)) return "bg-violet-500/15 text-violet-400";
  if (/new/.test(t)) return "bg-sky-500/15 text-sky-400";
  if (/(lost|dead|archiv|cold|pass)/.test(t)) return "bg-rose-500/15 text-rose-400";
  return "bg-slate-500/20 text-slate-300";
};

const CARD_ACTIONS = [
  { kind: "call", Icon: PhoneCall, title: "Log call" },
  { kind: "text", Icon: MessageSquare, title: "Log text" },
  { kind: "email", Icon: Mail, title: "Log email" },
  { kind: "follow_up", Icon: CalendarPlus, title: "Schedule follow-up" },
  { kind: "offer", Icon: FileText, title: "Log offer" },
  { kind: "note", Icon: ListChecks, title: "Add note" },
];

const daysAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days <= 0 ? "today" : `${days}d`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

function MetricCard({ m, data, month, active, onClick }) {
  const Icon = m.icon;
  const done = data?.done ?? 0, goal = data?.goal ?? 0;
  const weekly = data?.period === "week";
  const complete = goal > 0 && done >= goal;
  const pct = Math.min(100, goal ? (done / goal) * 100 : 0);
  return (
    <button onClick={onClick} data-testid={`metric-${m.key}`}
      className={`text-left rounded-md px-3 py-2.5 border transition-all ${active ? m.ring : "border-border/60 bg-secondary/20 hover:border-foreground/20"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          <Icon className={`h-3.5 w-3.5 ${m.accent}`} />{m.label}
        </div>
        {complete && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${m.accent}`}>{done}</span>
        <span className="text-sm text-muted-foreground">/ {goal}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full ${m.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground truncate">
        {complete ? <span className="text-emerald-400 font-medium">{weekly ? "Weekly goal met" : "Goal met"}</span>
          : goal === 0 ? "Not a business day · no daily goal"
          : `${data?.remaining ?? goal} remaining ${weekly ? "this week" : "today"}`}
      </div>
      {month && <MonthProgress m={m} month={month} />}
    </button>
  );
}

function PaceLine({ m, pace }) {
  if (pace === undefined || pace === null) return null;
  const n = Math.abs(pace);
  const unit = n === 1 ? m.unit[0] : m.unit[1];
  const tone = pace > 0 ? "text-emerald-400" : pace < 0 ? "text-rose-400" : "text-sky-400";
  const text = pace > 0 ? `${pace} ${unit} ahead of schedule` : pace < 0 ? `${-pace} ${unit} behind schedule` : "Right on pace";
  return <div className={`mt-0.5 text-[11px] font-medium truncate ${tone}`} data-testid={`metric-${m.key}-pace`}>{text}</div>;
}

function MonthProgress({ m, month }) {
  const done = month.done ?? 0, goal = month.goal ?? 0;
  const pct = Math.min(100, goal ? (done / goal) * 100 : 0);
  return (
    <div className="mt-3 pt-2.5 border-t border-border/60" data-testid={`metric-${m.key}-month`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
        <span>This month</span>
        <span><span className={`font-semibold ${m.accent}`} data-testid={`metric-${m.key}-month-done`}>{done}</span> / {goal}</span>
      </div>
      <div className="mt-1 h-1 rounded-full bg-border overflow-hidden">
        <div className={`h-full rounded-full ${m.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground truncate">
        {done >= goal ? <span className="text-emerald-400 font-medium">Monthly goal met</span> : `${month.remaining} to go · ${Math.round(pct)}%`}
      </div>
      <PaceLine m={m} pace={month.pace} />
    </div>
  );
}

function ItemRow({ title, subtitle, badge, badgeTone, onOpen, tid, extra }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-3 py-2.5" data-testid={tid}>
      <div className="min-w-[10rem] flex-1">
        <div className="text-sm font-medium truncate">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      {extra}
      {badge && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${badgeTone}`}>{badge}</span>}
      {onOpen && (
        <Button size="sm" variant="outline" onClick={onOpen} data-testid={`${tid}-open`}>
          Open<ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      )}
    </div>
  );
}

export default function CRM() {
  const navigate = useNavigate();
  const [three, setThree] = useState(null);
  const [queue, setQueue] = useState([]);
  const [roll, setRoll] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [leadBoard, setLeadBoard] = useState(null);
  const [tab, setTab] = useState("today");
  const location = useLocation();
  useEffect(() => {
    if (new URLSearchParams(location.search).get("view") === "pipeline") setTab("pipeline");
  }, [location.search, location.key]);
  const [confirmConvert, setConfirmConvert] = useState(null);
  const [converting, setConverting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeMetric, setActiveMetric] = useState("offers");
  const [logOpen, setLogOpen] = useState(false);
  const [preset, setPreset] = useState(null);

  const openLog = (card, kind) => { setPreset({ recordId: card.id, kind }); setLogOpen(true); };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/crm/369").then((r) => r.data).catch(() => null),
      api.get("/crm/work-queue").then((r) => r.data.items || []).catch(() => []),
      api.get("/crm/rollups").then((r) => r.data).catch(() => null),
      api.get("/crm/pipeline").then((r) => r.data).catch(() => null),
      api.get("/crm/lead-pipeline").then((r) => r.data).catch(() => null),
    ]).then(([t, q, rl, p, lb]) => {
      setThree(t); setQueue(q); setRoll(rl); setPipeline(p); setLeadBoard(lb); setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveLead = async (card, toStatus) => {
    const r = await api.post("/crm/lead-pipeline/move", { id: card.id, to_status: toStatus });
    if (toStatus === "Converted") {
      toast.success(`${card.name} converted to Property Screening`, {
        action: { label: "Open", onClick: () => navigate(r.data.open_path) },
      });
    } else toast.success(`${card.name} moved to ${toStatus}`);
    load();
  };

  const moveCard = async (id, toStatus) => {
    const card = (leadBoard?.cards || []).find((c) => c.id === id);
    if (!card || card.column === toStatus) return;
    if (card.converted) { toast.error("This lead is already converted"); return; }
    if (toStatus === "Converted") { setConfirmConvert(card); return; }
    try { await moveLead(card, toStatus); } catch (e) { toast.error(apiError(e)); }
  };

  const doConvert = async () => {
    setConverting(true);
    try { await moveLead(confirmConvert, "Converted"); setConfirmConvert(null); }
    catch (e) { toast.error(apiError(e)); } finally { setConverting(false); }
  };

  if (loading && !three) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const metricData = three?.[activeMetric];
  const fu = three?.followups;

  return (
    <div className={`${tab === "pipeline" ? "max-w-none" : "max-w-7xl"} mx-auto px-4 sm:px-6 py-6 space-y-6`} data-testid="crm-page">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">CRM</h1>
            <p className="text-sm text-muted-foreground">Your daily acquisition execution engine · {fmtDate(three?.date)}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <TabsList className="bg-transparent p-0 h-auto gap-2" data-testid="crm-tabs">
              <TabsTrigger value="today" data-testid="crm-tab-today"
                className="rounded-md border border-border bg-transparent px-3 py-1.5 text-muted-foreground data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:border-primary/40 data-[state=active]:shadow-none">
                <ListChecks className="h-4 w-4 mr-1.5" />3-6-9
              </TabsTrigger>
              <TabsTrigger value="pipeline" data-testid="crm-tab-pipeline"
                className="rounded-md border border-border bg-transparent px-3 py-1.5 text-muted-foreground data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:border-primary/40 data-[state=active]:shadow-none">
                <KanbanIcon className="h-4 w-4 mr-1.5" />Pipeline
              </TabsTrigger>
            </TabsList>
            <Button variant="outline" onClick={() => { setPreset(null); setLogOpen(true); }} data-testid="crm-log-activity-btn"><Plus className="h-4 w-4 mr-1.5" />Log Activity</Button>
          </div>
        </div>

        {/* ---------------- TODAY ---------------- */}
        <TabsContent value="today" className="space-y-4 mt-4">
          <Section title="3-6-9 Daily Goals" icon={Target} tid="section-369" defaultOpen>
            {three?.month && (
              <p className="text-xs text-muted-foreground mb-3" data-testid="month-goal-note">
                {three.month.label} goal · {three.month.business_days} business days · 3 offers per week · 6 outreach + 9 mailers per business day
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="three-six-nine">
              {METRICS.map((m) => (
                <MetricCard key={m.key} m={m} data={three?.[m.key]} month={three?.month?.[m.key]} active={activeMetric === m.key}
                  onClick={() => setActiveMetric(m.key)} />
              ))}
            </div>

            {/* Drilldown for the active metric */}
            <div className="mt-4" data-testid="metric-drilldown">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold" data-testid="metric-drilldown-title">{METRICS.find((x) => x.key === activeMetric)?.label} · {metricData?.period === "week" ? "this week" : "completed today"}</span>
                <Badge variant="secondary">{metricData?.items?.length || 0}</Badge>
              </div>
              {metricData?.stats && <MetricStats metric={activeMetric} stats={metricData.stats} />}
              {metricData?.items?.length ? (
                <div className="space-y-2">
                  {metricData.items.map((it) => (
                    <ItemRow key={it.id} tid={`drill-${it.id}`}
                      title={it.record_name || "Record"}
                      subtitle={`${it.contact_name ? it.contact_name + " · " : ""}${it.kind}${it.outcome ? " · " + it.outcome : ""}${it.notes ? " · " + it.notes : ""}`}
                      badge={it.source === "manual" ? "manual" : "auto"} badgeTone="bg-secondary text-muted-foreground"
                      extra={activeMetric === "outreach" ? <OutreachToggles it={it} onSaved={load} /> : null}
                      onOpen={it.open_path ? () => navigate(it.open_path) : null} />
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">Nothing logged yet {metricData?.period === "week" ? "this week" : "today"}. Complete work below to hit your goal.</p>}
            </div>
          </Section>

          <Section title="Follow-ups Due" count={(fu?.overdue?.length || 0) + (fu?.due_today?.length || 0)} tid="section-followups" defaultOpen>
            <p className="text-xs text-muted-foreground mb-3">No daily quota — follow-ups never count toward your 3-6-9. Overdue and active negotiations come first.</p>
            <div className="grid sm:grid-cols-3 gap-3" data-testid="followups-due">
              {[["overdue", "Overdue", "text-destructive", AlertTriangle], ["due_today", "Due today", "text-amber-400", CalendarClock], ["upcoming", "Upcoming", "text-muted-foreground", Clock]].map(([k, lbl, cls, Ico]) => (
                <div key={k} className="rounded-lg border border-border p-3" data-testid={`followup-${k}`}>
                  <div className={`flex items-center gap-1.5 text-xs font-semibold mb-2 ${cls}`}><Ico className="h-3.5 w-3.5" />{lbl} ({fu?.[k]?.length || 0})</div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto no-scrollbar">
                    {(fu?.[k] || []).slice(0, 20).map((c) => (
                      <button key={c.id} onClick={() => navigate(c.open_path)} data-testid={`followup-${k}-${c.id}`}
                        className="w-full text-left rounded-md border border-border/60 bg-background px-2.5 py-1.5 hover:border-foreground/20 transition-colors">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-medium truncate">{c.name}</div>
                          {c.negotiating && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 shrink-0">Negotiation</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{c.stage} · {c.status || "—"}</div>
                      </button>
                    ))}
                    {!(fu?.[k] || []).length && <p className="text-[11px] text-muted-foreground">None</p>}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Today's Work Queue" icon={ListChecks} count={queue.length} tid="section-workqueue" defaultOpen>
            <div data-testid="work-queue">
              {queue.length ? (
                <div className="space-y-2">
                  {queue.map((q, i) => {
                    const tone = q.type === "overdue_followup" ? "bg-destructive/15 text-destructive"
                      : q.type === "due_followup" ? "bg-amber-500/15 text-amber-400"
                      : q.type === "negotiation" ? "bg-sky-500/15 text-sky-400"
                      : "bg-secondary text-muted-foreground";
                    const label = { overdue_followup: "Overdue", due_followup: "Due today", negotiation: "Attention",
                      offer_target: "Offers", outreach_target: "Outreach", mailers_target: "Mailers" }[q.type] || q.type;
                    return <ItemRow key={q.id || `${q.type}-${i}`} tid={`wq-${i}`} title={q.title} subtitle={q.subtitle}
                      badge={label} badgeTone={tone} onOpen={q.open_path ? () => navigate(q.open_path) : null} />;
                  })}
                </div>
              ) : <p className="text-sm text-muted-foreground">All clear — no follow-ups due and daily targets met.</p>}
            </div>
          </Section>

          {roll && (
            <Section title="Weekly & Monthly Rollups" icon={TrendingUp} tid="section-rollups" defaultOpen={false}>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="rollups">
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-primary" /><span className="font-semibold">This Week</span></div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Stat label="Offers Sent" value={roll.week.offers} />
                    <Stat label="On-Market Outreach" value={roll.week.outreach} />
                    <Stat label="Off-Market Mailers" value={roll.week.mailers} />
                    <Stat label="Follow-ups" value={roll.week.followups} />
                    <Stat label="Goal Completion" value={`${roll.week.goal_completion_rate}%`} sub={`${roll.week.days_met}/${roll.week.days_elapsed} workdays`} />
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-primary" /><span className="font-semibold">This Month</span></div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Stat label="Offers Sent" value={roll.month.offers} />
                    <Stat label="On-Market Outreach" value={roll.month.outreach} />
                    <Stat label="Off-Market Mailers" value={roll.month.mailers} />
                    <Stat label="Follow-ups" value={roll.month.followups} />
                    <Stat label="Contracts" value={roll.month.contracts_generated} />
                    <Stat label="Acquisitions Closed" value={roll.month.acquisitions_closed} />
                  </div>
                </div>
              </div>
            </Section>
          )}
        </TabsContent>

        {/* ---------------- PIPELINE ---------------- */}
        <TabsContent value="pipeline" className="mt-4">
          {leadBoard && (
            <KanbanBoard
              columns={leadBoard.columns}
              items={leadBoard.cards}
              groupKey="column"
              onMove={moveCard}
              colorFor={(c) => STATUS_COLOR[c]}
              renderCard={(c) => (
                <div data-testid={`crm-card-${c.id}`} className="group">
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-semibold leading-tight truncate">{c.name}</div>
                        <button onClick={(e) => { e.stopPropagation(); navigate(c.open_path); }} data-testid={`crm-card-${c.id}-open`}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary shrink-0">
                          Open<ExternalLink className="h-3 w-3" />
                        </button>
                      </div>
                      {c.contact && <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5 min-w-0"><User className="h-3 w-3 shrink-0" /><span className="truncate">{c.contact}</span></div>}
                      {c.property_address && <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5 min-w-0"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{c.property_address}</span></div>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2 pl-6">
                    {c.market && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${c.market === "On-Market" ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"}`}>{c.market}</span>}
                    {c.status && <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusTone(c.status)}`}>{c.status}</span>}
                    {c.strategy && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary">{c.strategy}</span>}
                  </div>
                  {c.offer != null && <div className="text-[11px] text-muted-foreground pl-6 mt-1">Offer: <span className="text-foreground font-medium">{money(c.offer)}</span></div>}
                  <div className="flex items-center justify-between mt-1.5 pl-6 text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1">{c.next_follow_up && (<><CalendarClock className="h-3 w-3" />{String(c.next_follow_up).slice(0, 10)}</>)}</span>
                    <span>{daysAgo(c.last_activity)}</span>
                  </div>
                  {!c.converted && <div className="mt-2 pt-2 border-t border-border flex items-center justify-between">
                    {CARD_ACTIONS.map((a) => (
                      <button key={a.kind} title={a.title} data-testid={`crm-card-${c.id}-action-${a.kind}`}
                        onClick={(e) => { e.stopPropagation(); openLog(c, a.kind); }}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                        <a.Icon className="h-4 w-4" />
                      </button>
                    ))}
                  </div>}
                </div>
              )}
            />
          )}
          <p className="text-xs text-muted-foreground mt-3">Drag a lead to change its status. Dropping it in Converted creates its Property Screening record.</p>
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmConvert} onOpenChange={(o) => { if (!o) setConfirmConvert(null); }}>
        <DialogContent data-testid="convert-lead-dialog">
          <DialogHeader><DialogTitle>Convert {confirmConvert?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This creates a Property Screening record from this lead and marks the lead Converted.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmConvert(null)} data-testid="convert-lead-cancel">Cancel</Button>
            <Button onClick={doConvert} disabled={converting} data-testid="convert-lead-confirm">{converting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Convert"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LogActivityDialog open={logOpen} onOpenChange={setLogOpen} pipeline={pipeline} preset={preset} onLogged={load} />
    </div>
  );
}

function Section({ title, count, defaultOpen = true, children, tid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-border bg-secondary/30 overflow-hidden" data-testid={tid}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${tid}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-accent/40 transition-colors">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {count != null && <Badge variant="secondary">{count}</Badge>}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-4 border-t border-border">{children}</div>}
    </div>
  );
}


const STAT_DEFS = {
  outreach: [["attempts", "Attempts"], ["conversations", "Conversations"], ["responses", "Responses"]],
  mailers: [["sent", "Sent"], ["in_transit", "In transit"], ["delivered", "Delivered"], ["returned", "Returned"], ["responses", "Responses"], ["opportunities", "Opportunities"]],
};

function MetricStats({ metric, stats }) {
  const outcomes = Object.entries(stats.outcomes || {});
  return (
    <div className="mb-3" data-testid={`metric-${metric}-stats`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">This month</div>
      <div className="flex flex-wrap gap-2">
        {STAT_DEFS[metric].map(([k, lbl]) => (
          <div key={k} className="rounded-md border border-border/60 bg-background px-2.5 py-1.5" data-testid={`metric-${metric}-stat-${k}`}>
            <span className="text-sm font-semibold">{stats[k] ?? 0}</span>
            <span className="ml-1.5 text-[11px] text-muted-foreground">{lbl}</span>
          </div>
        ))}
      </div>
      {outcomes.length > 0 && (
        <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`metric-${metric}-outcomes`}>
          Outcomes: {outcomes.map(([o, n]) => `${o} (${n})`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function OutreachToggles({ it, onSaved }) {
  const flip = async (field) => {
    try { await api.patch(`/crm/activities/${it.id}`, { [field]: !it[field] }); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-1 shrink-0">
      {[["connected", "Conversation"], ["responded", "Responded"]].map(([f, lbl]) => (
        <button key={f} type="button" onClick={() => flip(f)} data-testid={`drill-${it.id}-${f}`}
          className={`text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${it[f] ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
          {lbl}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-border/60 bg-background px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

const KINDS = [
  { value: "offer", label: "Offer sent" },
  { value: "mailer", label: "Mailer sent" },
  { value: "call", label: "Call" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "dm", label: "Direct message" },
  { value: "follow_up", label: "Follow-up" },
  { value: "note", label: "Note" },
];

const CONTACT_KINDS = ["call", "text", "email", "dm"];
const KIND_HINT = {
  offer: "Counts toward your 3 weekly offers when logged on a Property Analysis record.",
  mailer: "Counts toward your 9 daily mailers when sent to an off-market owner.",
  contact: "The first outreach to an on-market record counts toward your 6. Later contact is a follow-up.",
  follow_up: "Follow-ups never count toward the 3-6-9.",
  note: "Notes are kept in history only.",
};
const EMPTY_QC = { contact_role: "Agent", contact_name: "", company: "", phone: "", email: "", street_address: "", city: "", state: "", postal_code: "", apn: "" };
const QC_FIELDS = [
  ["contact_name", "Contact name *", "Jane Smith", "col-span-2"], ["company", "Brokerage / company", "", "col-span-2"],
  ["phone", "Phone", "", ""], ["email", "Email", "", ""],
  ["street_address", "Property address *", "123 Main St", "col-span-2"],
  ["city", "City", "", ""], ["state", "State", "CA", ""], ["postal_code", "ZIP", "", ""], ["apn", "APN", "", ""],
];

function QuickContactFields({ qc, setQc }) {
  const set = (k) => (e) => setQc((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="space-y-2 rounded-md border border-border/60 p-3" data-testid="log-quick-contact">
      <div className="flex gap-2">
        {["Agent", "Wholesaler"].map((r) => (
          <button key={r} type="button" onClick={() => setQc((p) => ({ ...p, contact_role: r }))} data-testid={`log-role-${r.toLowerCase()}`}
            className={`rounded-full border px-3 py-0.5 text-[11px] font-medium transition-colors ${qc.contact_role === r ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {r}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {QC_FIELDS.map(([k, lbl, ph, span]) => (
          <div key={k} className={span}>
            <label className="text-[11px] font-medium text-muted-foreground">{lbl}</label>
            <Input value={qc[k]} onChange={set(k)} placeholder={ph} className="h-8" data-testid={`log-qc-${k}`} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">Creates an on-market Lead with this contact, then logs the outreach on it.</p>
    </div>
  );
}

const countedNote = (a) => ({
  offers: "Counted toward this week's offers",
  outreach: "Counted toward today's on-market outreach",
  mailers: "Counted toward today's off-market mailers",
}[a?.metric] || (a?.outreach_type === "follow_up" ? "Logged as a follow-up — not counted toward the 3-6-9" : "Logged to history — not counted toward the 3-6-9"));

function RecordPicker({ cards, value, onChange }) {
  const [open, setOpen] = useState(false);
  const sel = cards.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid="log-record-select"
          className="w-full justify-between font-normal">
          <span className={`truncate ${sel ? "" : "text-muted-foreground"}`}>{sel ? `${sel.stage} · ${sel.name}` : "Search a pipeline record"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search name, address, contact or stage…" data-testid="log-record-search" />
          <CommandList className="max-h-64">
            <CommandEmpty>No matching record.</CommandEmpty>
            <CommandGroup>
              {cards.map((c) => (
                <CommandItem key={c.id} value={`${c.name} ${c.property_address} ${c.contact} ${c.stage} ${c.status} ${c.id}`}
                  onSelect={() => { onChange(c.id); setOpen(false); }} data-testid={`log-record-opt-${c.id}`}>
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="min-w-0">
                    <div className="text-sm truncate">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{[c.stage, c.property_address, c.contact].filter(Boolean).join(" · ")}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function LogActivityDialog({ open, onOpenChange, pipeline, preset, onLogged }) {
  const cards = Object.values(pipeline?.cards || {}).flat();
  const [kind, setKind] = useState("call");
  const [recordId, setRecordId] = useState("");
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [connected, setConnected] = useState(false);
  const [responded, setResponded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("existing");
  const [qc, setQc] = useState(EMPTY_QC);
  const navigate = useNavigate();
  const { dup, setDup, run } = useDuplicateGuard();
  const isContact = CONTACT_KINDS.includes(kind);
  const isNew = mode === "new";

  useEffect(() => {
    if (!open) return;
    if (preset) {
      if (preset.recordId) setRecordId(preset.recordId);
      if (preset.kind) setKind(preset.kind);
    } else {
      setRecordId(""); setKind("call");
    }
    setMode("existing"); setQc(EMPTY_QC);
    setNotes(""); setOutcome(""); setNextAt(""); setConnected(false); setResponded(false);
  }, [open, preset]);

  const pickMode = (m) => { setMode(m); if (m === "new" && !isContact) setKind("call"); };
  // Noon local time so the chosen day never shifts across timezones.
  const common = () => ({
    notes, outcome: outcome || null,
    next_follow_up_at: nextAt ? new Date(`${nextAt}T12:00:00`).toISOString() : null,
    ...(isContact ? { connected, responded } : {}),
  });
  const done = () => { onOpenChange(false); onLogged(); };

  const submitNew = async (allow) => {
    const r = await api.post("/crm/outreach/quick", { ...qc, kind, ...common(), allow_duplicate: allow });
    setDup(null);
    toast.success(`Lead created · ${r.data.record.name}`, {
      description: countedNote(r.data),
      action: { label: "Open lead", onClick: () => navigate(r.data.record.open_path) },
    });
    done();
    return r;
  };

  const submit = async () => {
    if (isNew) {
      if (!qc.contact_name.trim() || !qc.street_address.trim()) { toast.error("Add the contact name and property address"); return; }
      setBusy(true);
      try { await run(submitNew); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
      return;
    }
    const card = cards.find((c) => c.id === recordId);
    if (!card) { toast.error("Pick a record"); return; }
    setBusy(true);
    try {
      const r = await api.post("/crm/activities", {
        kind, resource: card.resource, record_id: card.id, record_name: card.name,
        contact_name: card.contact, ...common(),
      });
      toast.success("Activity logged", { description: countedNote(r.data) });
      done();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const retryDup = async () => {
    setBusy(true);
    try { await dup.retry(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="log-activity-dialog">
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2" data-testid="log-mode">
            {[["existing", "Existing record"], ["new", "New agent / wholesaler"]].map(([m, lbl]) => (
              <button key={m} type="button" onClick={() => pickMode(m)} data-testid={`log-mode-${m}`}
                className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${mode === m ? "border-primary/40 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {lbl}
              </button>
            ))}
          </div>
          {isNew ? <QuickContactFields qc={qc} setQc={setQc} /> : (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Record</label>
              <RecordPicker cards={cards} value={recordId} onChange={setRecordId} />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Activity type</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="log-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.filter((k) => !isNew || CONTACT_KINDS.includes(k.value)).map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="log-kind-hint">{KIND_HINT[isContact ? "contact" : kind]}</p>
          </div>
          {isContact && (
            <div className="flex items-center gap-4" data-testid="log-contact-result">
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={connected} onCheckedChange={(v) => setConnected(!!v)} data-testid="log-connected" />Had a conversation</label>
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={responded} onCheckedChange={(v) => setResponded(!!v)} data-testid="log-responded" />Got a response</label>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What happened?" data-testid="log-notes" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Outcome</label>
              <Input value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="e.g. Left voicemail" data-testid="log-outcome" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Next follow-up</label>
              <Input type="date" value={nextAt} onChange={(e) => setNextAt(e.target.value)} data-testid="log-next-followup" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="log-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log Activity"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <DuplicateApnDialog dup={dup} busy={busy} onClose={() => setDup(null)} onCreateAnyway={retryDup} />
    </>
  );
}
